import React, { Fragment, useEffect } from "react";
import { useForm } from "react-hook-form";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import Spinner from "../FieldComponets/Spinner";
import { ErrorMessage } from "@hookform/error-message";
import { useDispatch, useSelector } from "react-redux";
import ToastDefault from "../FieldComponets/Toast";
import PageLayout from "../Layouts/PageLayout";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { decodeID, getLastURL } from "../../utils/Helper";
import {
  SubscriptionPlanDetailsAction,
  SubscriptionPlanUpdateAction,
} from "../../Redux/Action/SubscriptionPlanAction";
import { toast } from "react-toastify";

const formSchema = Yup.object({
  name: Yup.string()
    .required("Please enter subscription name.")
    .trim()
    .max(255),
  overview: Yup.string().default(""),
  price: Yup.number()
    .typeError("Price must be a number.")
    .min(0, "Price cannot be negative.")
    .required("Please enter price."),
  validity_days: Yup.number()
    .typeError("Validity must be a number.")
    .integer("Use whole days.")
    .min(1, "At least 1 day.")
    .required("Please enter validity."),
  max_services: Yup.string().default(""),
  max_staff: Yup.string().default(""),
  max_offline_booking_staff: Yup.string().default(""),
  enable_growth_statistics: Yup.boolean().default(true),
  enable_appointment_cancellation: Yup.boolean().default(true),
});

type FormInputs = Yup.InferType<typeof formSchema>;

function SubscriptionPlanEdit() {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const plan = useSelector((state: any) => state.SubscriptionPlanDetailsState?.data?.data);
  const updateState = useSelector((state: any) => state.SubscriptionPlanUpdateState);
  const log_in = useSelector((state: any) => state.AdminLogInState);
  const location = useLocation();
  const editId = getLastURL(location);

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<FormInputs>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      name: "",
      overview: "",
      price: 0,
      validity_days: 30,
      max_services: "",
      max_staff: "",
      max_offline_booking_staff: "",
      enable_growth_statistics: true,
      enable_appointment_cancellation: true,
    },
  });

  const planCode = plan?.plan_code;

  useEffect(() => {
    dispatch(
      SubscriptionPlanDetailsAction({
        detail_id: decodeID(editId),
      }),
    );
  }, [dispatch, editId]);

  useEffect(() => {
    if (plan?.id) {
      reset({
        name: plan.name,
        overview: plan.overview || "",
        price: Number(plan.price),
        validity_days: plan.validity_days,
        max_services:
          plan.max_services != null ? String(plan.max_services) : "",
        max_staff: plan.max_staff != null ? String(plan.max_staff) : "",
        max_offline_booking_staff:
          plan.max_offline_booking_staff != null
            ? String(plan.max_offline_booking_staff)
            : "",
        enable_growth_statistics: !!plan.enable_growth_statistics,
        enable_appointment_cancellation: !!plan.enable_appointment_cancellation,
      });
    }
  }, [plan, reset]);

  useEffect(() => {
    if (updateState.status) {
      setTimeout(() => navigate("/subscription-plans"), 800);
    }
  }, [updateState.status, navigate]);

  useEffect(() => {
    return () => {
      dispatch(SubscriptionPlanUpdateAction("RESET"));
    };
  }, [dispatch]);

  const onSubmit = (data: FormInputs) => {
    if (!plan?.id) return;

    const checkPositiveOrEmpty = (raw: string, label: string): boolean => {
      const t = String(raw ?? "").trim();
      if (t === "") return true;
      const n = parseInt(t, 10);
      if (!Number.isFinite(n) || n < 1) {
        toast.error(`${label}: enter a positive number or leave empty for unlimited.`);
        return false;
      }
      return true;
    };

    if (planCode === "basic") {
      const ms = parseInt(String(data.max_services).trim(), 10);
      const mst = parseInt(String(data.max_staff).trim(), 10);
      const mob = parseInt(String(data.max_offline_booking_staff).trim(), 10);
      if (!ms || ms < 1) {
        toast.error("Please enter a positive max services limit for Basic.");
        return;
      }
      if (!mst || mst < 1) {
        toast.error("Please enter a positive max staff limit for Basic.");
        return;
      }
      if (!mob || mob < 1) {
        toast.error("Please enter a positive offline booking staff limit for Basic.");
        return;
      }
    } else if (planCode === "premium") {
      if (
        !checkPositiveOrEmpty(data.max_services, "Max services") ||
        !checkPositiveOrEmpty(data.max_staff, "Max staff") ||
        !checkPositiveOrEmpty(data.max_offline_booking_staff, "Max staff for offline bookings")
      ) {
        return;
      }
    }

    const payload: any = {
      edit_id: plan.id,
      name: data.name,
      overview: data.overview,
      price: data.price,
      validity_days: data.validity_days,
      max_services: data.max_services,
      max_staff: data.max_staff,
      max_offline_booking_staff: data.max_offline_booking_staff,
      enable_growth_statistics: data.enable_growth_statistics ? 1 : 0,
      enable_appointment_cancellation: data.enable_appointment_cancellation ? 1 : 0,
    };
    dispatch(SubscriptionPlanUpdateAction(payload));
  };

  return (
    <Fragment>
      {log_in?.loading ? <Spinner /> : ""}
      <PageLayout
        title="Edit subscription plan"
        breadcrumbs={{
          secLast: { link: "/subscription-plans", name: "Subscription plans" },
        }}
        buttons={[{ url: "", className: "", text: "", show: false }]}
      >
        <div className="mt-2">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row gy-4 mb-2">
              <div className="col-md-6">
                <label className="form-label">Subscription name</label>
                <input type="text" className="form-control mb-0" {...register("name")} />
                <ErrorMessage
                  errors={errors}
                  name="name"
                  render={({ message }) => (
                    <p className="mt-1 mb-0" style={{ color: "red" }}>
                      {message}
                    </p>
                  )}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Plan type</label>
                <input
                  type="text"
                  className="form-control mb-0"
                  readOnly
                  disabled
                  value={plan?.plan_code === "basic" ? "Basic" : plan?.plan_code === "premium" ? "Premium" : "—"}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Subscription overview</label>
                <textarea className="form-control" rows={3} {...register("overview")} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Subscription price</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control mb-0"
                  {...register("price", { valueAsNumber: true })}
                />
                <ErrorMessage
                  errors={errors}
                  name="price"
                  render={({ message }) => (
                    <p className="mt-1 mb-0" style={{ color: "red" }}>
                      {message}
                    </p>
                  )}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Validity (days)</label>
                <input
                  type="number"
                  min={1}
                  className="form-control mb-0"
                  {...register("validity_days", { valueAsNumber: true })}
                />
                <ErrorMessage
                  errors={errors}
                  name="validity_days"
                  render={({ message }) => (
                    <p className="mt-1 mb-0" style={{ color: "red" }}>
                      {message}
                    </p>
                  )}
                />
              </div>
            </div>

            <h6 className="mt-3">Configurable limits</h6>
            <p className="small text-muted">
              <strong>Basic:</strong> all three limits are required (positive numbers).{" "}
              <strong>Premium:</strong> leave a field empty for <strong>unlimited</strong>, or set a cap
              (e.g. 100, 200). Changing limits applies to <strong>new</strong> subscriptions or newly
              onboarded salons; existing salons keep limits from when they subscribed.
            </p>

            <div className="row gy-4 mb-2">
              <div className="col-md-4">
                <label className="form-label">Max services</label>
                <input
                  type="number"
                  min={1}
                  className="form-control mb-0"
                  placeholder={planCode === "premium" ? "Empty = unlimited" : ""}
                  {...register("max_services")}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Max staff members</label>
                <input
                  type="number"
                  min={1}
                  className="form-control mb-0"
                  placeholder={planCode === "premium" ? "Empty = unlimited" : ""}
                  {...register("max_staff")}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Max staff for offline bookings</label>
                <input
                  type="number"
                  min={1}
                  className="form-control mb-0"
                  placeholder={planCode === "premium" ? "Empty = unlimited" : ""}
                  {...register("max_offline_booking_staff")}
                />
              </div>
            </div>

            <div className="row gy-3 mb-4">
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="growth"
                    {...register("enable_growth_statistics")}
                  />
                  <label className="form-check-label" htmlFor="growth">
                    Enable growth &amp; revenue statistics (month / year / previous year filters)
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="cancel"
                    {...register("enable_appointment_cancellation")}
                  />
                  <label className="form-check-label" htmlFor="cancel">
                    Enable cancelling upcoming appointments
                  </label>
                </div>
              </div>
            </div>

            <button type="submit" className="btn btn-primary">
              Save
            </button>
          </form>
        </div>
        <ToastDefault />
      </PageLayout>
    </Fragment>
  );
}

export default SubscriptionPlanEdit;
